import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requestSignupOTP, verifySignupOTP, saveSession } from "../service/auth";
import "../styles/Auth.css";

const BRAND_ICON = (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

export default function RegisterPage() {
  const navigate = useNavigate();

  /* ── State ── */
  const [step, setStep] = useState(1);         // 1 = name+email, 2 = OTP
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileType, setProfileType] = useState("client"); // client or consultant
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const otpRefs = useRef([]);
  const nameRef = useRef(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  /* ── Cooldown timer ── */
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  /* ── Step 1: send signup OTP ── */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) { setError("Full name must be at least 2 characters."); return; }
    if (!email.includes("@")) { setError("Please enter a valid email."); return; }
    setLoading(true);
    try {
      await requestSignupOTP(email, name.trim(), profileType);
      setStep(2);
      setResendCooldown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (err) {
      setError(err.message || "Failed to send OTP. This email may already be registered.");
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP input handling ── */
  const handleOtpChange = (idx, val) => {
    const c = val.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[idx] = c;
    setOtp(next);
    if (c && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) otpRefs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (text.length) {
      const next = text.split("").concat(["", "", "", "", "", ""]).slice(0, 6);
      setOtp(next);
      otpRefs.current[Math.min(text.length, 5)]?.focus();
    }
  };

  /* ── Step 2: verify signup OTP ── */
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the full 6-digit OTP."); return; }
    setLoading(true);
    try {
      await verifySignupOTP(email, code, profileType);
      alert("Registration successful! Please login to continue.");
      navigate("/login");
    } catch (err) {
      setError(err.message || "OTP verification failed. Please try again.");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      await requestSignupOTP(email, name.trim(), profileType);
      setResendCooldown(60);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card"> 
        {error && (
          <div className="auth-error" style={{ marginBottom: 16 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {/* ── Step 1: Name + Email ── */}
        {step === 1 && (
          <form className="auth-form" onSubmit={handleSendOTP}>
            <div className="auth-form__group">
              <label className="auth-form__label">Full Name</label>
              <input
                ref={nameRef}
                className="auth-form__input"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={e => setName(e.target.value)}
                minLength={2}
                required
                autoComplete="name"
              />
            </div>

            <div className="auth-form__group">
              <label className="auth-form__label">Email Address</label>
              <input
                className="auth-form__input"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="auth-form__group">
              <label className="auth-form__label">Register as</label>
              <div className="auth-role-grid">
                <div 
                  className={`auth-role-card ${profileType === 'client' ? 'active' : ''}`}
                  onClick={() => setProfileType('client')}
                >
                  <div className="auth-role-card__icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                    </svg>
                  </div>
                  <div className="auth-role-card__info">
                    <div className="auth-role-card__title">Client</div>
                  </div>
                </div>

                <div 
                  className={`auth-role-card ${profileType === 'consultant' ? 'active' : ''}`}
                  onClick={() => setProfileType('consultant')}
                >
                  <div className="auth-role-card__icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div className="auth-role-card__info">
                    <div className="auth-role-card__title">Consultant</div>
                  </div>
                </div>
              </div>
            </div>

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? <><div className="auth-spinner" /> Sending OTP…</> : "Send OTP →"}
            </button>

            <div className="auth-divider">or</div>

            <p className="auth-hint">
              Already have an account?{" "}
              <Link to="/login">Sign in</Link>
            </p>
          </form>
        )}

        {/* ── Step 2: OTP ── */}
        {step === 2 && (
          <form className="auth-form" onSubmit={handleVerifyOTP}>
            <p className="auth-hint" style={{ marginBottom: 4 }}>
              OTP sent to
            </p>
            <div className="auth-email-chip">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {email}
            </div>

            <div className="auth-otp-row" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={el => otpRefs.current[i] = el}
                  className={`auth-otp-input ${digit ? "filled" : ""}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  autoComplete="one-time-code"
                />
              ))}
            </div>

            <button className="auth-btn" type="submit" disabled={loading}>
              {loading ? <><div className="auth-spinner" /> Verifying…</> : "Verify & Create Account"}
            </button>

            <p className="auth-hint">
              Didn't receive it?{" "}
              <button type="button" onClick={handleResend} disabled={resendCooldown > 0 || loading}>
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
              </button>
            </p>

            <p className="auth-hint">
              <button type="button" onClick={() => { setStep(1); setOtp(["","","","","",""]); setError(""); }}>
                ← Edit details
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
